import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FormControl from '@mui/material/FormControl';
import { styled } from '@mui/material/styles';
import getPlaceholderColors from '../../utils/placeholderColors';
import { isPlaceholder } from '../../utils/PlaceholderUtil';
import PlaceholderChip from '../common/PlaceholderChip';

const Wrapper = styled('div')(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  padding: '2px 4px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 4,
  position: 'relative',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  cursor: 'text',
  minHeight: 36,

  '&:hover': {
    borderColor: theme.palette.text.disabled,
  },

  '&.focused': {
    borderColor: theme.palette.info.main,
    boxShadow: `0 0 0 2px ${theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)'}`,
  },
}));

const StyledInput = styled('input')(({ theme }) => ({
  fontSize: 14,
  fontFamily: '"Open Sans", sans-serif',
  height: 28,
  boxSizing: 'border-box',
  padding: '4px 4px',
  width: 0,
  minWidth: 60,
  flexGrow: 1,
  border: 0,
  margin: 0,
  outline: 0,
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,

  '&::placeholder': {
    color: theme.palette.text.disabled,
    opacity: 1,
  },
}));


const Dropdown = styled('ul')(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    width: '100%',
    margin: '4px 0 0',
    padding: '4px 0',
    position: 'absolute',
    top: '100%',
    left: 0,
    listStyle: 'none',
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    maxHeight: 220,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? '0 4px 16px rgba(0, 0, 0, 0.3), 0 1px 4px rgba(0, 0, 0, 0.2)'
      : '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
    zIndex: 10,
  };
});

const DropdownItem = styled('li')(({ theme }) => ({
  padding: '6px 10px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: 4,
  transition: 'background-color 0.1s ease',

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },

  '&.highlighted': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const EmptyState = styled('li')(({ theme }) => ({
  padding: '12px 10px',
  color: theme.palette.text.disabled,
  fontSize: 13,
  fontFamily: '"Open Sans", sans-serif',
  textAlign: 'center',
}));

const FreeTextChip = styled('span')(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    display: 'inline-flex',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    height: 26,
    lineHeight: '24px',
    borderRadius: 5,
    boxSizing: 'border-box',
    padding: '2px 8px',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: '"Open Sans", sans-serif',
    maxWidth: '100%',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8f8f8',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.2)' : '0 1px 2px rgba(0,0,0,0.04)',

    '& .chip-label': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },

    '& .chip-delete': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 4,
      cursor: 'pointer',
      borderRadius: '50%',
      padding: 2,
      flexShrink: 0,
      opacity: 0.7,
      transition: 'all 0.15s ease',
      '& svg': { fontSize: 14 },
      '&:hover': {
        opacity: 1,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      },
    },
  };
});

function buildPlaceholderOptions(placeholders, inputType) {
  if (!placeholders) return [];
  const result = [];
  for (const component of placeholders) {
    if (component.placeholders) {
      for (const ph of component.placeholders) {
        if (inputType === 'DATETIME' && ph.type !== 'datetime') continue;
        const colors = getPlaceholderColors(ph.type, ph.length);
        result.push({
          text: '${' + ph.key + '}',
          key: ph.key,
          type: ph.type,
          length: ph.length,
          borderColor: colors.default,
          backgroundColor: colors.hover,
          icon: colors.icon,
        });
      }
    } else if (component) {
      const colors = getPlaceholderColors(component.type, component.length);
      result.push({
        text: '${' + component.key + '}',
        key: component.key,
        type: component.type,
        length: component.length,
        borderColor: colors.default,
        backgroundColor: colors.hover,
        icon: colors.icon,
      });
    }
  }
  return result;
}

function getChipInfo(chipValue, placeholderMap, inputType) {
  const raw = typeof chipValue === 'string' ? chipValue : chipValue?.text || chipValue?.key || '';
  const label = typeof raw === 'string' ? raw.trim() : '';
  const match = placeholderMap[label];

  if (match) {
    const key = label.startsWith('${') && label.endsWith('}') ? label.slice(2, -1) : label;
    return { label, key, type: match.type, length: match.length, isValid: true, isPlaceholder: true };
  }

  if (isPlaceholder(label) || inputType === 'DATETIME') {
    const key = label.startsWith('${') && label.endsWith('}') ? label.slice(2, -1) : label;
    return { label, key, isValid: false, isPlaceholder: true };
  }

  return { label, key: label, isValid: true, isPlaceholder: false };
}

const ChipInput = ({
  inputValue = [],
  placeholders = [],
  onPlaceholderSelected,
  onDelete,
  onFocus,
  placeholder = '',
  fullWidth = false,
  inputType,
  style,
  className,
}) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  const options = useMemo(
    () => buildPlaceholderOptions(placeholders, inputType),
    [placeholders, inputType]
  );

  const placeholderMap = useMemo(() => {
    const map = {};
    for (const opt of options) {
      map[opt.text] = opt;
    }
    return map;
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(opt => opt.text.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [filteredOptions]);

  useEffect(() => {
    if (highlightIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('li');
      if (items[highlightIndex]) {
        items[highlightIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex]);

  const values = inputValue || [];

  const handleSelect = useCallback((option) => {
    if (onPlaceholderSelected) {
      const key = option.text.substring(2, option.text.length - 1);
      onPlaceholderSelected({
        key,
        style: {
          borderColor: option.borderColor,
          backgroundColor: option.backgroundColor,
        },
        icon: option.icon,
        isNotPlaceholder: false,
      });
    }
    setQuery('');
    setHighlightIndex(-1);
  }, [onPlaceholderSelected]);

  const handleFreeText = useCallback((text) => {
    if (!text || !text.trim()) return;
    if (onPlaceholderSelected) {
      onPlaceholderSelected({
        key: text.trim(),
        style: { borderColor: 'var(--color-border, #dadada)', backgroundColor: 'var(--color-bg-subtle, #f8f8f8)' },
        icon: null,
        isNotPlaceholder: true,
      });
    }
    setQuery('');
    setHighlightIndex(-1);
  }, [onPlaceholderSelected]);

  const handleKeyDown = useCallback((e) => {
    if (inputType === 'DATETIME') {
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown && filteredOptions.length > 0) {
        setHighlightIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown) {
        setHighlightIndex(prev => Math.max(prev - 1, 0));
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightIndex]);
      } else if (query.trim()) {
        handleFreeText(query);
      }
      return;
    }

    if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightIndex(-1);
      return;
    }

    if (e.key === 'Backspace' && !query && values.length > 0 && onDelete) {
      onDelete(values.length - 1);
    }
  }, [inputType, showDropdown, filteredOptions, highlightIndex, query, values, onDelete, handleSelect, handleFreeText]);

  const handleBlur = useCallback((e) => {
    if (wrapperRef.current && wrapperRef.current.contains(e.relatedTarget)) return;
    setFocused(false);
    if (query.trim() && inputType !== 'DATETIME') {
      handleFreeText(query);
    }
    setTimeout(() => {
      setShowDropdown(false);
      setHighlightIndex(-1);
    }, 150);
  }, [query, inputType, handleFreeText]);

  const handleFocusInput = useCallback((e) => {
    setFocused(true);
    setShowDropdown(true);
    if (onFocus) onFocus(e);
  }, [onFocus]);

  return (
    <FormControl fullWidth={fullWidth} style={style} className={className}>
      <Wrapper
        ref={wrapperRef}
        className={focused ? 'focused' : ''}
        onClick={() => inputRef.current?.focus()}
        onBlur={handleBlur}
      >
        {values.map((chip, index) => {
          const info = getChipInfo(chip, placeholderMap, inputType);
          if (info.isPlaceholder) {
            return (
              <PlaceholderChip
                key={info.label + '-' + index}
                placeholderKey={info.key}
                type={info.type}
                length={info.length}
                isValid={info.isValid}
                onDelete={onDelete ? () => onDelete(index) : undefined}
              />
            );
          }
          return (
            <FreeTextChip key={info.label + '-' + index}>
              <span className="chip-label">{info.label}</span>
              <span
                className="chip-delete"
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (onDelete) onDelete(index);
                }}
              >
                <CloseIcon />
              </span>
            </FreeTextChip>
          );
        })}
        <StyledInput
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocusInput}
          onKeyDown={handleKeyDown}
          placeholder={values.length === 0 ? placeholder : undefined}
        />
        {showDropdown && focused && (
          <Dropdown ref={dropdownRef}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, i) => (
                <DropdownItem
                  key={option.text}
                  className={i === highlightIndex ? 'highlighted' : ''}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                  onMouseEnter={() => setHighlightIndex(i)}
                >
                  <PlaceholderChip
                    placeholderKey={option.key}
                    type={option.type}
                    length={option.length}
                    style={{ cursor: 'pointer' }}
                  />
                </DropdownItem>
              ))
            ) : query ? (
              <EmptyState>No matching placeholders</EmptyState>
            ) : null}
          </Dropdown>
        )}
      </Wrapper>
    </FormControl>
  );
};

export default ChipInput;
